import {
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { BillingFilterComponent } from '../../billing-filter/billing-filter.component';
import { LookupService } from '../../../../services/lookup.service';
import { ListService } from '../../../../shared/services/list.service';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { BillingService } from '../../../../services/billing.service';
import { DatePipe } from '@angular/common';
import { SharedDataService } from '../../../../shared/services/shared-data-service';
import {
  STATIC_DATA,
  STATIC_JSON_DATA_PATH,
} from '../../../../shared/constants/static-data.constants';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { SectionsService } from '../../../../services/sections.service';
import { LoggerService } from '../../../../shared/services/logger.service';
import { Router, ActivatedRoute } from '@angular/router';
import {
  DialogConfig,
  DialogService,
} from '../../../../shared/services/dialog.service';
import { UserService } from '../../../../services/user.service';
import { ClaimReportComponent } from '../../claim-report/claim-report.component';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { ClaimHierarchyComponent } from '../../claim-hierarchy/claim-hierarchy.component';

@Component({
  selector: 'ppl-claims-list',
  standalone: true,
  imports: [
    ListTableComponent,
    BillingFilterComponent,
    ClaimReportComponent,
    NgxExtendedPdfViewerModule,
    ClaimHierarchyComponent,
  ],
  templateUrl: './claims-list.component.html',
  styleUrl: './claims-list.component.css',
})
export class ClaimsListComponent implements OnInit {
  sharedDataKey: string = STATIC_DATA.SHARED_CLAIMS_DATA;
  pageId: string = PAGE_CONSTANTS.CLAIMS_GRID_PAGE;
  showTable = false;
  statusList: any;
  programName!: string;
  processingStatusList: any;
  appliedFilters: any;
  claims: any[] = [];
  cdrData: any[] = [];
  paymentData: any[] = [];
  claimReportData: any[] = [];
  resubClaimId: any;
  resubReason: any;
  resubDate: any;
  hierarchyData: any;
  id: any;
  claimCols = [
    {
      field: 'code',
      header: 'Code',
      jsonpath: 'code',
    },
    {
      field: 'value',
      header: 'Value',
      jsonpath: 'value',
    },
  ];
  actionButtons: TableRowActionsModel = new TableRowActions({
    viewCDR: true,
    viewPdf: true,
    viewPayment: true,
    viewClaimReport: true,
    viewHierarchy: true,
  });
  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;
  @ViewChild('viewCDRDialog') viewCDRDialog!: TemplateRef<any>;
  @ViewChild('viewHierarchyDialog') viewHierarchyDialog!: TemplateRef<any>;
  @ViewChild('viewPaymentDialog') viewPaymentDialog!: TemplateRef<any>;
  @ViewChild('viewClaimReportDialog') viewClaimReportDialog!: TemplateRef<any>;
  @ViewChild('viewPdfDialog') viewPdfDialog!: TemplateRef<any>;
  @ViewChild('resubDialog') resubDialog!: TemplateRef<any>;
  private dialogSvc = inject(DialogService);

  fields = [
    { field: 'timeframe', show: true, default: '30 days' },
    { field: 'status', show: true },
    { field: 'pplIcn', show: true },
    { field: 'participantPplId', show: true },
    { field: 'authNumber', show: true },
    { field: 'medicaidCin', show: true },
    { field: 'timesheetId', show: true },
    { field: 'mcoName', show: true },
    { field: 'serviceCode', show: true },
    { field: 'modifier', show: true },
    { field: 'year', show: false },
    { field: 'fileType', show: false },
    { field: 'pplBatchId', show: false },
    { field: 'memberId', show: false },
    { field: 'extIcn', show: false },
    { field: 'claimStatus', show: false },
    { field: 'batchName', show: true },
    { field: 'resub', show: true },
    { field: 'serviceDate', show: true },
  ];

  lineItemCols = [
    {
      field: 'tasTimesheetlineDetailId',
      header: 'Timesheet ID',
      jsonpath: 'tasTimesheetlineDetailId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'billedUnits',
      header: 'Units',
      jsonpath: 'billedUnits',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'serviceCode',
      header: 'Service Code',
      jsonpath: 'serviceCode',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'modifierCode',
      header: 'Modifier',
      jsonpath: 'modifierCode',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'servicecodeRate',
      header: 'Pay Rate',
      jsonpath: 'servicecodeRate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'providerName',
      header: 'PA Name',
      jsonpath: 'providerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
  ];

  cols = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private listService: ListService,
    private billingService: BillingService,
    private lookupService: LookupService,
    private datePipe: DatePipe,
    private sharedDataService: SharedDataService,
    private logger: LoggerService,
    private sectionService: SectionsService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.getColumnData();
    this.statusList = this.lookupService.getLookupValue('Business Status');
    this.processingStatusList = this.lookupService.getLookupValue('TS_App_S');
    const navigationState = window.history.state;

    if (navigationState.batchId) {
      const { batchId, pmpmflg } = navigationState;
      const filters = [];
      filters.push({
        jsonPath: 'pmpmFlg',
        value: pmpmflg,
        operator: 'Equal',
      });
      filters.push({
        jsonPath: 'tasBillingBatchMasterId',
        value: batchId,
        operator: 'Equal',
      });
      this.searchClaims(filters);
    } else {
      console.log('No state data available');
    }
  }

  async getColumnData() {
    try {
      const tempCols = await this.sectionService.getJsonConfigurationData(
        STATIC_JSON_DATA_PATH.CLAIMS_LIST_PAGE_HEADERS,
        'billing'
      );
      this.cols = tempCols.columns;
    } catch (e) {
      this.logger.debug('Error in getting column data', e);
    }
  }
  onSearch(filters: any) {
    this.appliedFilters = filters;
    this.searchClaims(filters);
  }

  getCDRData(icn: any): void {
    const payload = {
      pageName: 'billingCdrFileDetailView',
      programCode: 'NY',
      condition: 'and',
      recordsPerPage: 20,
      startingPageNumber: 1,
      searchParam: [
        {
          jsonPath: 'tasBillingBatchClaimDetailId',
          value: icn,
          operator: 'Equal',
        },
      ],
      orderByColumn: ['createddate'],
      orderBy: ['desc'],
    };

    this.listService.getListData(payload, 'billing').subscribe((data: any) => {
      if (data.status === 200) {
        const records = data.responsedata;
        records.forEach((element: any) => {
          element.createddate = this.datePipe.transform(
            element.createddate,
            'MM/dd/yyyy'
          );
        });
        this.cdrData = records;
        this.openViewCDRPopup();
      }
    });
  }

  getPaymentData(icn: any): void {
    const payload = {
      pageName: 'billingPaymentFileDetailListView',
      programCode: 'NY',
      condition: 'and',
      recordsPerPage: 20,
      startingPageNumber: 1,
      searchParam: [
        {
          jsonPath: 'tasBillingBatchClaimDetailId',
          value: icn,
          operator: 'Equal',
        },
      ],
      orderByColumn: ['paymentDate'],
      orderBy: ['desc'],
    };

    this.listService.getListData(payload, 'billing').subscribe((data: any) => {
      if (data.status === 200) {
        this.paymentData = data.responsedata;
        const records = data.responsedata;
        records.forEach((element: any) => {
          element.paymentAmount = element.paymentAmount
            ? this.billingService.formatCurrency(element.paymentAmount)
            : element.paymentAmount;
          element.patientPaidAmount = element.patientPaidAmount
            ? this.billingService.formatCurrency(element.patientPaidAmount)
            : element.patientPaidAmount;
        });

        this.openViewPaymentPopup();
      }
    });
  }

  getClaimReportData(id: any): void {
    const payload = {
      reportName: 'fetchBatchClaims',
      mcoName: null,
      batchDate: null,
      reportBy: null,
      claimType: null,
      batchClaimId: id,
      claimFieldName: null,
      programCode: this.programName,
    };

    this.billingService
      .getClaimReportGridData(payload)
      .subscribe((data: any) => {
        if (data.status === 200) {
          this.claimReportData = data.responsedata;
          this.openViewClaimReportPopup();
        }
      });
  }

  buildRequestPayload(
    pageName: string,
    orderBy: string,
    searchParams: [],
    startingPageNumber = 1
  ) {
    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: 20,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: orderBy ? [orderBy] : [],
      orderBy: ['desc'],
    };
    return payload;
  }
  searchClaims(filters: any, startingPageNumber = 1, currentPage = 0) {
    if (startingPageNumber == 1) {
      this.claims = [];
    }
    const processedPayload = this.buildRequestPayload(
      'billingClaimsListView',
      'createddate',
      filters,
      startingPageNumber
    );
    this.listService
      .getListData(processedPayload, 'billing')
      .subscribe((data: any) => {
        const records = data.responsedata;
        records.forEach((element: any) => {
          element.serviceDate = this.datePipe.transform(
            element.serviceDate,
            'MM/dd/yyyy'
          );
          element.createddate = this.datePipe.transform(
            element.createddate,
            'MM/dd/yyyy'
          );
          element.paymentAmount = element.paymentAmount
            ? this.billingService.formatCurrency(element.paymentAmount)
            : element.paymentAmount;
          element.totalCharges = element.totalCharges
            ? this.billingService.formatCurrency(element.totalCharges)
            : element.totalCharges;
        });
        if (startingPageNumber > 1) {
          this.claims = [...this.claims, ...records];
        } else {
          this.claims = records;
        }
        if (this.childComponent) {
          this.childComponent.resetPagination(startingPageNumber, currentPage);
        }
      });
  }

  rowClicked(event: any) {
    if (event.loaded) {
      event.showLineItem = !event.showLineItem;
      return;
    }
    const claimId = event.tasBillingBatchClaimDetailId;
    this.getClaimDetails(claimId, event);
  }

  getHierarchyData(data: any) {
    const payload = { ...data };
    this.billingService.getHierarchyData(payload).subscribe((data: any) => {
      if (data.status === 200) {
        this.hierarchyData = data.responsedata;
        this.openViewHierarchyPopup(payload.pplIcn);
      }
    });
  }

  onAction(event: any) {
    if (event.action === 'viewCDR') {
      this.getCDRData(event.data.tasBillingBatchClaimDetailId);
    }
    if (event.action === 'viewPdf') {
      this.getPdfBlob(event.data.tasBillingBatchClaimDetailId);
    }
    if (event.action === 'viewPayment') {
      this.getPaymentData(event.data.tasBillingBatchClaimDetailId);
    }
    if (event.action === 'viewClaimReport') {
      this.id = event.data.tasBillingBatchClaimDetailId;
      this.getClaimReportData(event.data.tasBillingBatchClaimDetailId);
    }
    if (event.action === 'viewHierarchy') {
      const data = {
        programCode: this.programName,
        pplIcn: event.data.pplIcn,
      };
      this.getHierarchyData(data);
    }
  }
  pdfSrc: string | null = null;
  pdfMessage: string | null = null;
  getPdfBlob(claimId: any) {
    const payload = {
      reportName: 'fetchBatchClaims',
      mcoName: null,
      batchDate: null,
      reportBy: null,
      claimType: null,
      batchClaimId: claimId,
      claimFieldName: null,
      programCode: this.programName,
    };
    this.billingService.getPDF(payload).subscribe({
      next: (blob) => {
        if (blob.type === 'application/pdf') {
          this.pdfMessage = null;
          const url = URL.createObjectURL(blob);
          this.pdfSrc = url;
          this.openPdfPopup();
        } else {
          this.pdfMessage = 'No valid PDF forms metadata found.';
          this.openPdfPopup();
        }
      },
      error: (err) => {
        console.error('Failed to fetch PDF:', err);
      },
    });
  }

  onPageLoad(event: any) {
    this.searchClaims(
      this.appliedFilters,
      event.startingPageNumber,
      event?.first ?? 0
    );
  }
  rowActions(event: any) {
    if (event.col.field === 'resubFlg' && event.event.resubFlg === 'Yes') {
      const id = event.event.tasBillingBatchClaimDetailId;
      this.billingService
        .getResubDetails(this.programName, id)
        .subscribe((data: any) => {
          this.resubClaimId = data.responsedata[0].pplIcn;
          this.resubReason = data.responsedata[0].resubReason;
          this.resubDate = data.responsedata[0].resubDate;
          this.openResubDetailsDialog();
        });
    }
  }

  getClaimDetails(claimId: any, event: any) {
    this.billingService
      .getClaimDetails(this.programName, claimId)
      .subscribe((data: any) => {
        event.lineItems = data.responsedata;
        event.showLineItem = true;
        event.loaded = true;
      });
  }

  clearData() {
    this.sharedDataService.clearData(this.sharedDataKey);
  }

  openResubDetailsDialog() {
    const dialogConfig: DialogConfig = {
      title: 'Resubmission Details',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.resubDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  openViewCDRPopup() {
    const dialogConfig: DialogConfig = {
      title: 'CDR Recon',
      isOkText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
      styleClass: 'mrg-12',
    };

    this.dialogSvc.openDialog(
      this.viewCDRDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  openViewHierarchyPopup(icn: any) {
    const dialogConfig: DialogConfig = {
      title: `Hierarchy - ${icn}`,
      isOkText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
      styleClass: 'mrg-12',
    };

    this.dialogSvc.openDialog(
      this.viewHierarchyDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  openViewPaymentPopup() {
    const dialogConfig: DialogConfig = {
      title: 'Payment Recon',
      isOkText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
      styleClass: 'mrg-12',
    };

    this.dialogSvc.openDialog(
      this.viewPaymentDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  openViewClaimReportPopup() {
    const dialogConfig: DialogConfig = {
      title: 'Claim Report',
      size: 'large',
      position: 'top',
      showClose: true,
      showButtons: false,
      styleClass: 'mrg-12 split-auth-data',
    };

    this.dialogSvc.openDialog(
      this.viewClaimReportDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  openPdfPopup() {
    const dialogConfig: DialogConfig = {
      title: 'Form',
      size: 'large',
      position: 'top',
      showClose: true,
      showButtons: false,
      styleClass: 'mrg-12 split-auth-data',
    };

    this.dialogSvc.openDialog(
      this.viewPdfDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  handleCancel() {}
}
